import { useMemo, useState } from 'react'
import {
  Alert,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { KeyOutlined, SettingOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { formatMoneyValue, majorToMinor, minorToMajor } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  fetchPspProviders,
  resyncPspFingerprint,
  updatePspProvider,
  type PspProvider,
  type PspSecretHealth,
} from '@/features/payments/pspApi'

const HEALTH_COLOR: Record<PspSecretHealth, string> = {
  OK: 'green',
  MISMATCH: 'red',
  MISSING: 'orange',
}

interface FormValues {
  name: string
  state: 'ACTIVE' | 'SUSPENDED'
  sandbox: boolean
  minAmount: number
  maxAmount: number
  ipAllowlist: string
  rateLimitPerMinute: number
}

/**
 * To'lov provayderlari (PSP) sozlamasi.
 *
 * <h2>⚠️ MAXFIY KALIT BU YERDA KO'RSATILMAYDI VA O'RNATILMAYDI</h2>
 * Kalit servis konfiguratsiyasida (`xsib.payments.secrets.<code>`),
 * bazada esa faqat uning SHA-256 barmoq izi. Sabab texnik va uni
 * aylanib o'tib bo'lmaydi: imzo HMAC-SHA256, ya'ni tekshirish uchun
 * kalitning O'ZI kerak — hash bilan HMAC hisoblab bo'lmaydi.
 *
 * Shuning uchun panel faqat XULOSA ko'rsatadi:
 *   OK       — kalit sozlangan va barmoq iziga mos
 *   MISMATCH — sozlangan, lekin BOSHQA kalit. To'lovlar 401 oladi
 *   MISSING  — umuman sozlanmagan
 *
 * `MISMATCH` — bu sahifaning eng qimmatli qismi: aks holda bu holat
 * faqat PSP "imzolaringiz o'tmayapti" deb shikoyat qilganda, ya'ni
 * to'lovlar TO'XTAGANIDAN KEYIN bilinardi.
 *
 * <h2>Nima YO'Q va nega</h2>
 * Provayder QO'SHISH va O'CHIRISH yo'q. Yangi PSP uch tomonlama amal:
 * baza qatori + konfiguratsiyadagi kalit + deploy. Faqat birinchisini
 * paneldan qilish "PSP qo'shildi" degan yolg'on holat yaratardi: qator
 * bor, kalit yo'q, har so'rov 401.
 */
export function PaymentProvidersPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  const [editing, setEditing] = useState<PspProvider | null>(null)
  const [rotating, setRotating] = useState<PspProvider | null>(null)

  const providers = useQuery({
    queryKey: queryKeys.paymentProviders.all,
    queryFn: ({ signal }) => fetchPspProviders(signal),
  })

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing === null) throw new Error('no provider')
      const currency = editing.minAmount.currency
      return updatePspProvider(editing.id, {
        name: values.name.trim(),
        state: values.state,
        sandbox: values.sandbox,
        // Forma MAJOR birlikda ishlaydi (operator "30 000" deb yozadi),
        // backend esa MINOR kutadi. O'girish AYNAN shu yerda.
        minAmount: majorToMinor(values.minAmount, currency),
        maxAmount: majorToMinor(values.maxAmount, currency),
        ipAllowlist: values.ipAllowlist
          .split(/[\s,]+/)
          .map((v) => v.trim())
          .filter((v) => v !== ''),
        rateLimitPerMinute: values.rateLimitPerMinute,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentProviders.all })
      setEditing(null)
    },
    // ⚠️ `onError` ATAYLAB yo'q: bu mutatsiya OYNAGA bog'langan
    // (`confirmLoading={save.isPending}`) va xatosi sahifa banneriga
    // chiqsa, modal niqobi ortida qolardi.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeEditor = () => {
    save.reset()
    setEditing(null)
  }

  const columns = useMemo<ColumnsType<PspProvider>>(
    () => [
      {
        // Ro'yxat sahifalanmaydi (`pagination={false}`) — raqam `index` dan.
        key: '__rowNo',
        title: t('dataTable.rowNo'),
        width: 56,
        align: 'center',
        render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
      },
      { key: 'code', title: t('psp.code'), width: 130, render: (_, r) => <code>{r.code}</code> },
      { key: 'name', title: t('psp.name'), width: 180, render: (_, r) => r.name },
      {
        key: 'state',
        title: t('psp.state'),
        width: 130,
        render: (_, r) => (
          <Space size={4} wrap>
            <Tag color={r.state === 'ACTIVE' ? 'green' : 'default'}>
              {t(`pspState.${r.state}`)}
            </Tag>
            {r.sandbox ? <Tag color="blue">{t('psp.sandbox')}</Tag> : null}
          </Space>
        ),
      },
      {
        key: 'secret',
        title: t('psp.secret'),
        width: 160,
        render: (_, r) => (
          <Tooltip title={t(`psp.secretHealthHint.${r.secretHealth}`)}>
            <Tag color={HEALTH_COLOR[r.secretHealth]}>{t(`psp.secretHealth.${r.secretHealth}`)}</Tag>
          </Tooltip>
        ),
      },
      {
        key: 'limits',
        title: t('psp.limits'),
        width: 220,
        render: (_, r) => `${formatMoneyValue(r.minAmount)} — ${formatMoneyValue(r.maxAmount)}`,
      },
      {
        key: 'ip',
        title: t('psp.ipAllowlist'),
        width: 220,
        render: (_, r) =>
          r.ipAllowlist.length === 0 ? (
            // Bo'sh ro'yxat ishlab chiqarishda HAMMANI rad etadi — bu
            // "cheklov yo'q" emas, teskarisi. Neytral ko'rinishda
            // qoldirish operatorni chalg'itardi.
            <Tooltip title={t('psp.ipEmptyHint')}>
              <Tag color={r.sandbox ? 'blue' : 'red'}>{t('psp.ipEmpty')}</Tag>
            </Tooltip>
          ) : (
            <Space size={4} wrap>
              {r.ipAllowlist.map((ip) => (
                <Tag key={ip}>{ip}</Tag>
              ))}
            </Space>
          ),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, r) => (
          <RowActions
            items={[
              {
                key: 'edit',
                label: t('psp.edit'),
                icon: <SettingOutlined />,
                onClick: () => {
                  setEditing(r)
                  form.setFieldsValue({
                    name: r.name,
                    state: r.state,
                    sandbox: r.sandbox,
                    minAmount: minorToMajor(r.minAmount.amount, r.minAmount.currency),
                    maxAmount: minorToMajor(r.maxAmount.amount, r.maxAmount.currency),
                    ipAllowlist: r.ipAllowlist.join('\n'),
                    rateLimitPerMinute: r.rateLimitPerMinute,
                  })
                },
                permission: 'psp.configure',
              },
              {
                key: 'resync',
                label: t('psp.resync'),
                icon: <KeyOutlined />,
                onClick: () => setRotating(r),
                permission: 'psp.configure',
              },
            ]}
          />
        ),
      },
    ],
    [t, form],
  )

  const mismatched = (providers.data ?? []).filter((p) => p.secretHealth !== 'OK')

  return (
    <>
      <PageHeader title={t('nav.paymentProviders')} description={t('pageIntro.paymentProviders')} />

      {/*
        ⚠️ Bu ogohlantirish sahifaning asosiy qiymati: kalit nomuvofiqligi
        aks holda faqat to'lovlar to'xtaganda bilinardi.
      */}
      {mismatched.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('psp.secretProblemTitle')}
          description={t('psp.secretProblemText', {
            codes: mismatched.map((p) => p.code).join(', '),
          })}
        />
      ) : null}

      <Card>
        {/*
          ⚠️ Xato holati AJRATILGAN. Bu sahifada u ayniqsa qimmat:
          «Provayderlar yo'q» degan yozuv to'lov qabul qilish umuman
          sozlanmagan degan ma'noni berardi va operator PSP'ni qaytadan
          ulashga urinardi — aslida shunchaki so'rov yiqilgan edi.
        */}
        {providers.isError ? (
          <QueryErrorState error={providers.error} onRetry={() => void providers.refetch()} />
        ) : (
          <Table<PspProvider>
            rowKey={(r) => r.id}
            loading={providers.isLoading}
            dataSource={providers.data ?? []}
            columns={columns}
            pagination={false}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: (
                <EmptyState variant="no-data" title={t('psp.empty')} description={t('psp.emptyHint')} />
              ),
            }}
          />
        )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          {t('psp.noCreateHint')}
        </Typography.Paragraph>
      </Card>

      <Modal
        open={editing !== null}
        title={t('psp.editTitle', { code: editing?.code ?? '' })}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={save.isPending}
        onCancel={closeEditor}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      >
        {save.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(save.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('psp.editNotice')}
            description={t('psp.editNoticeText')}
          />

          <Form.Item name="name" label={t('psp.name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="state" label={t('psp.state')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'ACTIVE', label: t('pspState.ACTIVE') },
                { value: 'SUSPENDED', label: t('pspState.SUSPENDED') },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="sandbox"
            label={t('psp.sandbox')}
            valuePropName="checked"
            extra={t('psp.sandboxHint')}
          >
            <Switch />
          </Form.Item>

          <Space size="middle" style={{ display: 'flex' }}>
            <Form.Item name="minAmount" label={t('psp.minAmount')} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="maxAmount" label={t('psp.maxAmount')} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="ipAllowlist"
            label={t('psp.ipAllowlist')}
            extra={t('psp.ipAllowlistHint')}
          >
            <Input.TextArea rows={4} placeholder="213.230.90.0/24" />
          </Form.Item>

          <Form.Item
            name="rateLimitPerMinute"
            label={t('psp.rateLimit')}
            rules={[{ required: true }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <ResyncModal provider={rotating} onClose={() => setRotating(null)} />
    </>
  )
}

/**
 * Barmoq izini moslashtirish.
 *
 * ⚠️ `Input.Password` `visibilityToggle` SIZ — `NasSecretModal` bilan bir
 * xil qoida: sir hech qachon ekranda ochiq turmaydi.
 */
function ResyncModal({ provider, onClose }: { provider: PspProvider | null; onClose: () => void }) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ secret: string }>()
  const runner = useOperationRunner([queryKeys.paymentProviders.all])

  return (
    <Modal
      open={provider !== null}
      title={t('psp.resyncTitle', { code: provider?.code ?? '' })}
      okText={t('psp.resync')}
      cancelText={t('common.cancel')}
      confirmLoading={runner.running}
      destroyOnHidden
      onCancel={() => {
        form.resetFields()
        runner.clear()
        onClose()
      }}
      onOk={() =>
        submitOnValid(form, async (values) => {
          if (provider === null) return
          const outcome = await runner.run(() =>
            resyncPspFingerprint(provider.id, values.secret),
          )
          if (outcome.status !== 'FAILED') form.resetFields()
        })
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('psp.resyncWarnTitle')}
          description={t('psp.resyncWarnText')}
        />

        {runner.result ? <OperationResultAlert partialNote={false} result={runner.result} /> : null}

        <Form form={form} layout="vertical">
          <Form.Item
            name="secret"
            label={t('psp.secretField')}
            extra={t('psp.secretFieldHint')}
            rules={[{ required: true, message: t('psp.secretRequired') }]}
          >
            <Input.Password visibilityToggle={false} autoComplete="off" />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
